import { useState } from "react";
import PropTypes from "prop-types";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldSet,
  FieldLegend,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Constants from "constants/Constants";

const ENABLED_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const toEnabledOption = isAllowed => {
  if (isAllowed === undefined) return "any";
  return isAllowed ? "yes" : "no";
};

const FilterForm = ({ filters, onApply }) => {
  const [route, setRoute] = useState(filters.path ?? "");
  const [methods, setMethods] = useState(filters.method ?? []);
  const [enabled, setEnabled] = useState(toEnabledOption(filters.is_allowed));

  const toggleMethod = (method, checked) =>
    setMethods(current =>
      checked ? [...current, method] : current.filter(m => m !== method)
    );

  const handleApply = () =>
    onApply({
      ...(route && { path: route }),
      ...(methods.length > 0 && { method: methods }),
      ...(enabled !== "any" && { is_allowed: enabled === "yes" }),
    });

  return (
    <>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="permission-filter-route">Route</FieldLabel>
          <Input
            id="permission-filter-route"
            value={route}
            onChange={e => setRoute(e.target.value)}
          />
        </Field>
        <FieldSet>
          <FieldLegend variant="label">Method</FieldLegend>
          <div className="tw:grid tw:grid-cols-3 tw:gap-2">
            {Constants.HTTP_METHODS.map(method => (
              <Field key={method} orientation="horizontal">
                <Checkbox
                  id={`permission-filter-${method}`}
                  checked={methods.includes(method)}
                  onCheckedChange={checked => toggleMethod(method, checked)}
                />
                <FieldLabel htmlFor={`permission-filter-${method}`}>
                  {method.toUpperCase()}
                </FieldLabel>
              </Field>
            ))}
          </div>
        </FieldSet>
        <Field>
          <FieldLabel>Enabled</FieldLabel>
          <Select
            items={ENABLED_OPTIONS}
            value={enabled}
            onValueChange={setEnabled}
          >
            <SelectTrigger className="tw:w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {ENABLED_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      </FieldGroup>
      <DialogFooter>
        <Button onClick={handleApply}>Apply</Button>
      </DialogFooter>
    </>
  );
};

FilterForm.propTypes = {
  filters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
};

const PermissionFilterDialog = ({ open, onOpenChange, filters, onApply }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="tw:sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Filter Permissions</DialogTitle>
      </DialogHeader>
      <FilterForm filters={filters} onApply={onApply} />
    </DialogContent>
  </Dialog>
);

PermissionFilterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default PermissionFilterDialog;
